import { useEffect } from "react";
import { _ROUTES } from "../../../utils/_routes_constants";
import { formDataToRow } from "./utils";
import { DataTable } from "../../ui/DataTable";
import { buildIRI } from "../../../utils/function";
import { useBudgetData } from "../../../hooks/useBudgetData";
import {
  calculateMonthValue,
  refreshDependentCells,
  renderMonthCell,
} from "../../../utils/AgGridUtils/cellCalculate";
import { _COLORS } from "../../../utils/constants";

export const Table = ({
  rows,
  setRows,
  params,
  isDataLoading,
  onDataLoadingStart,
  onDataLoadingStop,
  gridRef,
}) => {
  const { loadData, handleCellValueChanged } = useBudgetData({
    setRows,
    onDataLoadingStart,
    onDataLoadingStop,
    params,
    formDataToRow,
  });

  useEffect(() => {
    loadData(_ROUTES.IPT_HIRE_EQUIPMENT_GROUP);
  }, [params.activity]);

  const generateHireEquipmentPayload = ({ colDef, data, newValue }) => {
    const field = colDef.field;
    const month = parseInt(field.replace("months.", ""));

    const payload = {
      amount: newValue || 0,
      theme: data.theme,
      subTheme: data.sub_theme,
      site: buildIRI("/sites", params.site),
      activity: buildIRI("/activities", params.activity),
      fiscalMonth: month,
      fiscalYear: params.fiscalYear,
      version: params.version || 1,
    };

    if (data.fiscal_month_id_mapping && data.fiscal_month_id_mapping[month]) {
      payload.id = data.fiscal_month_id_mapping[month];
    }

    return payload;
  };

  const handleCellChange = (agParams) => {
    handleCellValueChanged(
      agParams,
      generateHireEquipmentPayload,
      _ROUTES.IPT_HIRE_EQUIPMENT,
    );
  };

  return (
    <DataTable
      isDataLoading={isDataLoading}
      onCellValueChanged={handleCellChange}
      rows={rows}
      gridRef={gridRef}
      columnsIncludeMonths={true}
      columns={[
        {
          headerName: "",
          field: "theme",
          hide: true,
          rowGroup: true,
          suppressHeaderMenuButton: true,
        },
        {
          headerName: "",
          field: "sub_theme",
          minWidth: 180,
          suppressHeaderMenuButton: true,
        },
        {
          headerName: "",
          field: "title",
          showRowGroup: "theme",
          minWidth: 300,
          suppressHeaderMenuButton: true,
        },
      ]}
      columnsAfterMonths={[
        {
          headerName: "Total",
          headerClass: "total-header-col",
          field: "total",
          editable: false,
          cellDataType: "number",
          valueFormatter: (params) => {
            if (!params?.data?.months) {
              return " ";
            }

            if (
              params.data.multiply_tags?.length > 0 ||
              params.data.sum_tags?.length > 0
            ) {
              // Calculate total of all month values for calculation-based rows
              let rowTotal = 0;

              for (let month = 1; month <= 12; month++) {
                const monthValue = calculateMonthValue(params, month);
                if (monthValue !== null) {
                  rowTotal += monthValue;
                }
              }

              return rowTotal;
            } else {
              // Sum month values for regular rows
              const total = Object.values(params.data.months).reduce(
                (acc, curr) => acc + (curr || 0),
                0,
              );

              return total === 0 ? " " : total;
            }
          },
        },
      ]}
      columnsMonthAdditionalArgs={{
        editable: (params) => params.data?.editable === true,
        onCellValueChanged: (params) => {
          const colId = params.column.getColId();
          const currentMonth = parseInt(colId.split(".")[1]);
          refreshDependentCells(params, currentMonth);
        },
        cellRenderer: (params) => {
          return renderMonthCell(params, true);
        },
        valueFormatter: (params) => {
          return renderMonthCell(params);
        },
      }}
      agGridProps={{
        groupDisplayType: "groupRows",
        getRowStyle: (params) => {
          if (params.data === undefined) {
            return { backgroundColor: _COLORS.groupBgColor };
          } else if (params.data?.isTotal) {
            return { backgroundColor: _COLORS.totalLabourRow };
          }
        },
      }}
    />
  );
};
